import { Shop } from '../lib/supabase/api/shopService';
import { PrintSettings } from '../types/documentTypes';

export interface CompatibilityResult {
  isCompatible: boolean;
  reasons: string[];
}

/**
 * Generate print settings code from user's print settings
 * 
 * FORMAT: PPDO (4 digits) - Matches existing system in documentGroupsStore.ts
 * 
 * - PP (digits 1-2): Paper type × 1000
 *   - 0xxx = A4
 *   - 1xxx = A3  
 *   - 2xxx = Letter
 *   - 3xxx = Legal
 * 
 * - D (digit 3): Duplex mode × 100
 *   - x0xx = Single-sided (simplex)
 *   - x1xx = Double-sided (duplex)
 * 
 * - O (last 2 digits): Orientation + Color
 *   - xx00 = Portrait, B&W
 *   - xx01 = Landscape, B&W
 *   - xx10 = Portrait, Color
 *   - xx11 = Landscape, Color
 * 
 * EXAMPLES:
 * - 0000 = A4, Simplex, Portrait, B&W
 * - 0010 = A4, Simplex, Portrait, Color
 * - 0110 = A4, Duplex, Portrait, Color
 * - 1111 = A3, Duplex, Landscape, Color
 */
function generatePrintSettingsCode(settings: PrintSettings): number {
  const paperTypes: { [key: string]: number } = {
    'A4': 0,
    'A3': 1,
    'Letter': 2,
    'Legal': 3
  };

  let code = 0;
  
  // Orientation: portrait=0, landscape=1 (default to portrait if undefined)
  if (settings.orientation === 'landscape') {
    code += 1;
  }
  
  // Color: BW=0, Color=10
  if (settings.color) {
    code += 10;
  }
  
  // Duplex: simplex=0, duplex=100
  if (settings.doubleSided) {
    code += 100;
  }
  
  // Paper type: multiply by 1000 (default to A4 if undefined)
  const paperTypeCode = paperTypes[settings.paperType || 'A4'] || 0;
  code += paperTypeCode * 1000;

  return code;
}

/**
 * Check if a shop supports the user's print settings
 * @param shop Shop data from backend with supported_settings
 * @param userSettings User's selected print settings for all documents
 * @returns Compatibility result with reasons for incompatibility
 */
export function checkShopCompatibility(
  shop: Shop,
  userSettings: { [documentId: string]: PrintSettings }
): CompatibilityResult {
  const reasons: string[] = [];

  // If shop has no supported_settings data, assume compatible (fallback for older shops)
  if (!shop.supported_settings || typeof shop.supported_settings !== 'object') {
    return { isCompatible: true, reasons: [] };
  }

  // Get all unique print settings codes from user's documents
  const userPrintSettingsCodes = new Set<number>();
  const paperTypesUsed = new Set<string>();

  Object.values(userSettings).forEach(settings => {
    const code = generatePrintSettingsCode(settings);
    userPrintSettingsCodes.add(code);
    if (settings.paperType) {
      paperTypesUsed.add(settings.paperType);
    }
  });

  // Get all supported settings codes from all printers in the shop
  const supportedCodes = new Set<number>();
  
  Object.values(shop.supported_settings).forEach((printer: any) => {
    if (printer.supportedPrintSettings && Array.isArray(printer.supportedPrintSettings)) {
      printer.supportedPrintSettings.forEach((code: number) => {
        supportedCodes.add(code);
      });
    }
  });

  // Check if shop supports all required settings
  const requiredCodesArray = Array.from(userPrintSettingsCodes);
  for (const requiredCode of requiredCodesArray) {
    if (!supportedCodes.has(requiredCode)) {
      // Decode the settings code to provide a meaningful reason
      const paperTypeCode = Math.floor(requiredCode / 1000);
      const duplexCode = Math.floor((requiredCode % 1000) / 100);
      const lastTwoDigits = requiredCode % 100;
      const orientationCode = lastTwoDigits % 10;
      const colorCode = Math.floor(lastTwoDigits / 10);
      
      const paperTypes = ['A4', 'A3', 'Letter', 'Legal'];
      const paperType = paperTypes[paperTypeCode] || 'Unknown';
      const duplexMode = duplexCode === 1 ? 'Double-sided' : 'Single-sided';
      const orientation = orientationCode === 1 ? 'Landscape' : 'Portrait';
      const colorMode = colorCode === 1 ? 'Color' : 'B&W';
      
      reasons.push(`${paperType} ${duplexMode} ${orientation} ${colorMode}`);
    }
  }

  // Check if shop has pricing for required paper types
  if (shop.pricing) {
    const paperTypesArray = Array.from(paperTypesUsed);
    for (const paperType of paperTypesArray) {
      if (!shop.pricing[paperType]) {
        reasons.push(`${paperType} pricing not available`);
      }
    }
  }

  return {
    isCompatible: reasons.length === 0,
    reasons: Array.from(new Set(reasons)) // Remove duplicates
  };
}
